import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import { useIntl } from "react-intl";

import SingleQuestionAutosaveMultiselect from "@components/forms/SingleQuestionAutosaveMultiselect";
import SingleQuestionAutosaveRadio from "@components/forms/SingleQuestionAutosaveRadio";
import useOpener from "@hooks/useOpener";
import {
  CareProfileGuidanceTypeEnum,
  CareProfileTypesOfSupportNeededEnum,
  CareProfileUpdateDocument,
  CareProfileUpdateMutation,
  CareProfileUpdateMutationVariables,
  CareProfileUrgencyEnum,
  GetJourneyWithPlanQuery
} from "@typing/Generated";
import { optionsForGuidanceType, optionsForTypesOfSupportNeeded, optionsForUrgency } from "@utils/optionUtils";

type Props = {
  journey: GetJourneyWithPlanQuery["journey"];
};

const ClientNeedsForm = ({ journey }: Props) => {
  const intl = useIntl();

  const typesOfSupportNeededOpener = useOpener();
  const urgencyOpener = useOpener();
  const guidanceTypeOpener = useOpener();
  const [updateCareProfile] = useMutation<CareProfileUpdateMutation, CareProfileUpdateMutationVariables>(
    CareProfileUpdateDocument
  );

  const handleSelectCareProfileTypesOfSupportNeeded = useCallback(
    (typesOfSupportNeeded: CareProfileTypesOfSupportNeededEnum[]) =>
      updateCareProfile({
        variables: {
          id: journey.careProfile.id,
          typesOfSupportNeeded
        }
      }),
    [journey.careProfile.id, updateCareProfile]
  );

  const handleSelectCareProfileUrgency = useCallback(
    (urgency: CareProfileUrgencyEnum) =>
      updateCareProfile({
        variables: {
          id: journey.careProfile.id,
          urgency
        }
      }),
    [journey.careProfile.id, updateCareProfile]
  );

  const handleSelectCareProfileGuidanceType = useCallback(
    (guidanceType: CareProfileGuidanceTypeEnum) =>
      updateCareProfile({
        variables: {
          guidanceType,
          id: journey.careProfile.id
        }
      }),
    [journey.careProfile.id, updateCareProfile]
  );

  return (
    <div>
      <div className="margin-20-bottom">
        <SingleQuestionAutosaveMultiselect<CareProfileTypesOfSupportNeededEnum>
          currentSelections={journey.careProfile.typesOfSupportNeeded}
          onSelect={handleSelectCareProfileTypesOfSupportNeeded}
          opener={typesOfSupportNeededOpener}
          options={optionsForTypesOfSupportNeeded(intl)}
          questionKey="forms.careProfile.typesOfSupportNeededLabel"
        />
      </div>
      <div className="margin-20-bottom">
        <SingleQuestionAutosaveRadio<CareProfileUrgencyEnum>
          currentSelection={journey.careProfile.urgency}
          onSelect={handleSelectCareProfileUrgency}
          opener={urgencyOpener}
          options={optionsForUrgency(intl)}
          questionKey="forms.careProfile.urgencyLabel"
        />
      </div>
      <div>
        <SingleQuestionAutosaveRadio<CareProfileGuidanceTypeEnum>
          currentSelection={journey.careProfile.guidanceType}
          onSelect={handleSelectCareProfileGuidanceType}
          opener={guidanceTypeOpener}
          options={optionsForGuidanceType(intl)}
          questionKey="forms.careProfile.guidanceTypeLabel"
        />
      </div>
    </div>
  );
};

export default ClientNeedsForm;
